import React, { useState } from 'react';
import API from '../services/api_config';

// In a single-coach pilot (a `context` is passed) a thumbs down also offers
// one-tap reasons, and each press is recorded against this reply. Everywhere
// else the thumbs behave as they always have.
const REASONS = [
  { key: 'not_understood', label: "Didn't understand me" },
  { key: 'not_relevant', label: 'Not relevant' },
  { key: 'dismissive', label: 'Felt dismissive' },
  { key: 'unwanted_religion', label: "Religious content I didn't choose" },
  { key: 'other', label: 'Something else' },
];

const FeedbackButton = ({ sessionId, messageIndex, onFeedback, context, replyText }) => {
  const [submitted, setSubmitted] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [sentId, setSentId] = useState(null);
  const [asking, setAsking] = useState(false);
  const [keepCopy, setKeepCopy] = useState(true);
  const small = context ? '14px' : '12px';

  const sendReply = (id, helpful, why, keep) => {
    if (!context) return;
    API.post('/api/v1/feedback/reply', {
      conversation_id: id,
      message_index: typeof messageIndex === 'number' ? messageIndex : -1,
      helpful: helpful,
      reason: why || null,
      reply_text: (!helpful && keep) ? (replyText || null) : null,
      context: context
    }).catch((error) => console.error('Error submitting reply feedback:', error));
  };

  const handleFeedback = async (helpful) => {
    const id = sessionId || `session_${Date.now()}`;
    setSentId(id);
    setFeedback(helpful);
    setSubmitted(true);
    if (context && !helpful) setAsking(true);
    sendReply(id, helpful, null, true);

    try {
      await API.post('/api/v1/feedback', {
        session_id: id,
        helpful: helpful,
        rating: helpful ? 5 : 1
      });
      if (onFeedback) onFeedback(helpful);
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const chooseReason = (key) => { setAsking(false); sendReply(sentId, false, key, keepCopy); };
  const dropCopy = () => { setKeepCopy(false); sendReply(sentId, false, null, false); };

  const wrap = { marginTop: '12px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.2)' };

  if (submitted && asking) {
    return (
      <div style={{ ...wrap, paddingTop: '10px' }}>
        <div style={{ fontSize: '15px', fontWeight: 600, color: 'rgba(255,255,255,0.92)', marginBottom: '8px' }}>
          What was off about this reply?
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {REASONS.map((r) => (
            <button key={r.key} type="button" onClick={() => chooseReason(r.key)}
              style={{ padding: '8px 14px', borderRadius: '18px', border: '1px solid rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.08)', color: 'white', fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit' }}>
              {r.label}
            </button>
          ))}
          <button type="button" onClick={() => setAsking(false)}
            style={{ padding: '8px 10px', border: 'none', background: 'transparent', color: 'rgba(255,255,255,0.7)', fontSize: '15px', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit' }}>
            Skip
          </button>
        </div>
        <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginTop: '10px', lineHeight: 1.45 }}>
          {keepCopy ? (
            <>
              This reply is saved for review, to improve the coach. Your own messages are not included.{' '}
              <button type="button" onClick={dropCopy}
                style={{ padding: 0, border: 'none', background: 'transparent', color: 'rgba(255,255,255,0.9)', fontSize: '14px', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit' }}>
                Don&apos;t save it
              </button>
            </>
          ) : 'This reply will not be saved.'}
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={{ ...wrap, display: 'flex', alignItems: 'center', gap: '6px', fontSize: small, color: '#10B981' }}>
        {feedback ? '👍' : '👎'} Thanks for the feedback
      </div>
    );
  }

  return (
    <div style={{ ...wrap, display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ fontSize: small, color: 'rgba(255,255,255,0.7)' }}>
        Helpful?
      </span>
      <button
        onClick={() => handleFeedback(true)}
        style={{
          background: 'rgba(34,197,94,0.1)',
          border: '1px solid rgba(34,197,94,0.5)',
          borderRadius: '6px',
          padding: '4px 10px',
          cursor: 'pointer',
          fontSize: '16px',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(34,197,94,0.3)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(34,197,94,0.1)'}
        title="Yes, this was helpful"
      >
        👍
      </button>
      <button
        onClick={() => handleFeedback(false)}
        style={{
          background: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.5)',
          borderRadius: '6px',
          padding: '4px 10px',
          cursor: 'pointer',
          fontSize: '16px',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239,68,68,0.3)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
        title="No, this wasn't helpful"
      >
        👎
      </button>
    </div>
  );
};

export default FeedbackButton;
